import axios, { AxiosError } from 'axios';
import { type App, ref } from 'vue';
import { UploadFileInjectKey } from './symbols';

export default (app: App) => {
  const FILE_INPUT_EL_ID = 'file-input';

  const fileName = ref(null);
  const isUploading = ref(false);
  const isUploadProgressing = ref(false);
  const isDoneUploading = ref(false);
  const meta = ref<Record<string, unknown>>(null);
  const uploadProgress = ref(0);
  const error = ref(null);

  const onFileSelected = (event) => {
    fileName.value = (event.target as HTMLInputElement).files[0].name;
  };

  const prepareFileInput = () => {
    const inputElement = document.createElement('input');
    inputElement.setAttribute('type', 'file');
    inputElement.setAttribute('style', 'display:none');
    inputElement.setAttribute('id', FILE_INPUT_EL_ID);

    inputElement.addEventListener('change', onFileSelected);

    document.body.appendChild(inputElement);

    inputElement.click();
  };

  const abortController = new AbortController();
  const onBeforeUnload = ($event: BeforeUnloadEvent) => {
    if (!isUploading.value) return;
    $event.returnValue = 'Are you sure you want to leave? There is an upload in progress.';
  };

  const cleanupFileUpload = () => {
    const inputElement = document.getElementById(FILE_INPUT_EL_ID) as HTMLInputElement;
    fileName.value = null;
    if (inputElement == null) return;
    inputElement.removeEventListener('change', onFileSelected);
    document.body.removeChild(inputElement);
  };

  const resetState = () => {
    isUploading.value = false;
    isUploadProgressing.value = false;
    uploadProgress.value = 0;
  };

  const uploadFile = async (
    url,
    headers: {
      [key: string]: string;
    },
  ) => {
    const inputElement = document.getElementById(FILE_INPUT_EL_ID) as HTMLInputElement;
    const file = inputElement.files.item(0);
    if (file == null) return;

    isUploading.value = true;
    isDoneUploading.value = false;

    try {
      const response = await axios.post(url, file, {
        headers,
        onUploadProgress: (progressEvent) => {
          uploadProgress.value = Math.round((progressEvent.loaded * 100) / progressEvent.total);
          isUploadProgressing.value = uploadProgress.value < 100 && uploadProgress.value > 0;
        },
        signal: abortController.signal,
      });

      // If the response is not 200, we set the error and abort the upload
      if (response.status !== 200) {
        error.value = response.statusText;
      } else {
        isDoneUploading.value = true;
      }
    } catch (e) {
      const axiosError = e as AxiosError;
      error.value = axiosError.message;
    } finally {
      resetState();
      cleanupFileUpload();
    }
  };

  const cancelUpload = () => {
    abortController.abort();
    resetState();
    cleanupFileUpload();
  };

  window.addEventListener('beforeunload', onBeforeUnload);

  const uploadFilePlugin = {
    error,
    fileName,
    meta,
    isUploading,
    isUploadProgressing,
    uploadProgress,
    isDoneUploading,
    prepareFileInput,
    uploadFile,
    cancelUpload,
    cleanupFileUpload,
  };

  app.config.globalProperties.$upload = uploadFilePlugin;
  app.provide(UploadFileInjectKey, uploadFilePlugin);
};
